import BookBetService from '../../services/BookBetService';
import API from '../../utils/api';
import betSlipCalculator from '../../utils/betSlipCalculator';
import * as types from './types';
import {
    ENDPOINT_URL,
    device_URI,
    MINIMUM_ODD_VALUE,
    PRODUCER_TYPES,
    ERROR_CODES,
    BET_PLACEMENT_SUBMIT_BUTTON_TYPES,
} from '../../constants/defaultValues';
import { getTranslation } from '../../utils/translation';
import { isEmpty } from '../../utils/merger';
import { showErrorNotification } from '../../utils/render';
import { BET_SLIP_MAX_STAKE_LOADING, BET_SLIP_VALIDATIONS_CHANGED, UPDATE_BET_SLIP_BET_LINES } from './types';
import { validateBetLines } from '../reducers/betSlipReducer';

export const setRequestId = payload => ({
    type: types.BET_SLIP_REQUEST_ID_CHANGED,
    payload,
});

export const changeBetSlipActiveTab = tab => ({
    type: types.CHANGE_BET_SLIP_ACTIVE_TAB,
    payload: tab,
});

export const changeBetSlipOddDecision = decision => ({
    type: types.CHANGE_BET_SLIP_ODD_CHANGE_DECISION,
    payload: decision,
});

export const addBetSlipBetLine = betLine => (dispatch, getStore) => {
    const reduxStore = getStore();

    if (
        betLine.closed ||
        (!isEmpty(betLine.marketStatus) && betLine.marketStatus !== 0) ||
        (!isEmpty(betLine.outcomeStatus) && betLine.outcomeStatus !== 0) ||
        betLine.odd < MINIMUM_ODD_VALUE ||
        reduxStore.betSlip.isTicketSubmitting
    ) {
        console.error(`Failed to select outcome`, {
            betLine,
        });
        return;
    }

    dispatch({
        type: types.ADD_BET_SLIP_BET_LINE,
        payload: betLine,
    });
};

export const deleteBetSlipBetLine = betLine => ({
    type: types.DELETE_BET_SLIP_BET_LINE,
    payload: betLine,
});

export const changeBetLineStake = betLine => ({
    type: types.CHANGE_BET_SLIP_BET_LINE_STAKE,
    payload: betLine,
});

export const changeBetSlipStakes = ({ type, stake }) => ({
    type: types.BET_SLIP_STAKES_CHANGED,
    payload: { type, stake },
});

export const changeBetSlipCombinations = combinations => ({
    type: types.BET_SLIP_COMBINATIONS_CHANGED,
    payload: combinations,
});

export const fetchBetSlipEvents =
    ({ setLoading = true } = {}) =>
    async (dispatch, getState) => {
        const betLines = getState().betSlip.betLines;

        if (betLines.length === 0) return;

        if (setLoading) {
            dispatch({
                type: types.BET_SLIP_EVENTS_LOADING,
                payload: true,
            });
        }

        await API.post(`${ENDPOINT_URL}/bet-slip`, {
            markets: betLines.map(betLine => ({
                marketId: betLine.marketId,
                outcomeId: betLine.outcomeId,
            })),
        })
            .then(async res => {
                const { info, item } = res.data;
                if (!info.success) return;
                const { betSlipItems } = item;
                dispatch({
                    type: types.BET_SLIP_EVENTS_LOADED,
                    payload: betSlipItems,
                });
            })
            .catch(() =>
                dispatch({
                    type: types.BET_SLIP_EVENTS_LOADING,
                    payload: false,
                }),
            );
    };

export const clearBetSlip = () => dispatch => {
    dispatch({
        type: types.CLEAR_BET_SLIP,
    });
};

export const printTicket = async ticket => {
    try {
        const response = await API.post(`${device_URI}/api/print/ticket`, ticket);

        return response;
    } catch (error) {
        console.error('Printing ticket failed');
    }
};

export const printCoupon = async coupon => {
    try {
        const response = await API.post(`${device_URI}/api/print/coupon`, coupon);

        return response;
    } catch (error) {
        console.error('Printing ticket failed');
    }
};

export const printVoucher = async voucher => {
    try {
        const response = await API.post(`${device_URI}/api/print/voucher`, voucher);

        return response;
    } catch (error) {
        console.error('Printing voucher failed');
    }
};

export const setBetBanco = betLine => ({
    type: types.SET_BET_BANCO,
    payload: betLine,
});

export const startPlaceBetRequest = () => ({
    type: types.PLACE_BET_REQUEST,
});

export const bookBet = ticketDetails => async dispatch => {
    dispatch({
        type: types.PLACE_BET_REQUEST,
    });

    try {
        const res = await API.post(`${ENDPOINT_URL}/book-bet`, {
            ...ticketDetails,
        });
        const { info, item } = res.data;

        if (!info.success) {
            showErrorNotification(getTranslation('generalErrorModalTitle'));
            dispatch({
                type: types.PLACE_BET_FAILED,
                payload: info?.errors,
            });
            return;
        }

        const generatedCoupon = {
            ...ticketDetails,
            couponNumber: item.ticketBarCode,
            creationDate: item.creationDateTime,
        };

        dispatch({
            type: types.BET_SLIP_PLACED_TICKET_CHANGED,
            payload: generatedCoupon,
        });

        return generatedCoupon;
    } catch (error) {
        console.log(error.response, 'Error@/agent/tickets');
        const errors = error?.response?.data?.info?.errors;
        showErrorNotification(getTranslation('generalErrorModalTitle'));
        dispatch({
            type: types.PLACE_BET_FAILED,
            payload: errors || [],
        });
    }
};

export const placeBet = ticketDetails => async dispatch => {
    dispatch(startPlaceBetRequest());

    const requestId = '000' + (Date.now() * Math.random() * 10000000).toString().slice(0, 13);

    const body = {
        ...ticketDetails,
        printableResponse: false,
    };

    try {
        await API.post(`${ENDPOINT_URL}/tickets`, body, {
            headers: {
                'x-request-id': requestId,
            },
        });

        dispatch(
            setRequestId({
                requestId,
                createDate: new Date().getTime(),
            }),
        );
    } catch (error) {
        const apiError = error.response?.data?.info?.errors?.[0];

        if (apiError !== undefined) {
            dispatch(setPlaceBetErrors([apiError]));
        }
    }
};

export const getBetDetails = requestId => async (dispatch, getStore) => {
    try {
        const res = await BookBetService.getTicketByRequestId({ requestId });

        const { item } = res;

        dispatch(setRequestId(null));
        dispatch(setPlacedTicket(item));

        const couponBarcode = getStore().betSlip.couponBarcode;
        printTicket({ ...item, couponBarcode });
    } catch (error) {
        const apiError = error.response?.data?.info?.errors?.[0];

        if (apiError !== undefined) {
            dispatch(setRequestId(null));

            if (apiError.code === ERROR_CODES.oddsChanged) {
                dispatch(changeBetSlipSubmitButtonType(BET_PLACEMENT_SUBMIT_BUTTON_TYPES.confirm));
            } else if (apiError.code === ERROR_CODES.mtsTicketCreationInProgress) {
                setTimeout(() => {
                    dispatch(getBetDetails(requestId));
                }, 2000);
            } else {
                dispatch(setPlaceBetErrors([apiError]));
            }
        } else {
            setTimeout(() => {
                dispatch(getBetDetails(requestId));
            }, 2000);
        }
    }
};

export const setPlacedTicket = ticketData => ({
    type: types.BET_SLIP_PLACED_TICKET_CHANGED,
    payload: ticketData,
});

export const setPlaceBetErrors = errors => ({
    type: types.PLACE_BET_FAILED,
    payload: errors,
});

export const changeBetSlipSubmitButtonType = buttonType => ({
    type: types.BET_SLIP_SUBMIT_BUTTON_CHANGED,
    payload: buttonType,
});

export const updateBetLines = betLines => ({
    type: UPDATE_BET_SLIP_BET_LINES,
    payload: betLines,
});

export const validateBetSlip = betSlipState => (dispatch, getState) => {
    const validations = getState().betSlip.validations;

    const newValidations = validateBetLines({
        activeTab: betSlipState.activeTab,
        betLines: betSlipState.betLines,
        currentBalance: betSlipState.currentBalance,
        validations,
        selectedCombinations: betSlipState.selectedCombinations,
        stakeLimits: betSlipState.stakeLimits,
        stakes: betSlipState.stakes,
        isVoucherMode: betSlipState.isVoucherMode,
    });

    dispatch({
        type: BET_SLIP_VALIDATIONS_CHANGED,
        payload: newValidations,
    });
};

export const setBetSLipMaxStake = state => ({
    type: BET_SLIP_MAX_STAKE_LOADING,
    payload: state,
});

export const getMaxStake = betLine => async (dispatch, getState) => {
    dispatch(setBetSLipMaxStake(true));

    const betSlipState = getState().betSlip;
    const {
        betLines: allBetLines,
        activeTab,
        stakes,
        selectedCombinations,
        selectedOddChangeDecision,
    } = betSlipState;

    const betLines = betLine ? [betLine] : allBetLines;

    const { totalOdds, totalStake, expectedBetsCount, potentialPayout } = betSlipCalculator.calculateTicketDetails({
        betLines,
        activeTab,
        stakes,
        selectedCombinations,
    });
    const ticketData = {
        totalStake: totalStake,
        totalOdds: totalOdds,
        expectedBets: expectedBetsCount,
        ticketType: activeTab,
        acceptanceType: selectedOddChangeDecision,
        maxPaymentAmount: potentialPayout,
        bets: betLines.map(betLine => ({
            ...betLine,
            marketName: betLine.marketTemplateName,
            marketLabel: betLine.marketLabel,
            producerType: betLine.isLive ? PRODUCER_TYPES.live : PRODUCER_TYPES.preMatch,
        })),
        systemValues: selectedCombinations.map(system => system + betSlipCalculator.getBankers(betLines).length),
        printableResponse: false,
    };

    try {
        const response = await BookBetService.getMaxStake(ticketData);

        return response.item;
    } catch (error) {
        console.error('Could not get max stake response', error);
        return null;
    } finally {
        dispatch(setBetSLipMaxStake(false));
    }
};
